import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageLoader } from "@/components/common";
import Shell from "@/components/Shell";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import SearchPage from "@/pages/Search";

import Dashboard from "@/pages/student/Dashboard";
import Courses from "@/pages/student/Courses";
import CourseDetail from "@/pages/student/CourseDetail";
import TestCatalog from "@/pages/student/TestCatalog";
import TestSeries from "@/pages/student/TestSeries";
import Pyp from "@/pages/student/Pyp";
import ExamInterface from "@/pages/student/ExamInterface";
import Result from "@/pages/student/Result";
import Performance from "@/pages/student/Performance";
import { Saved, CurrentAffairs, Subscriptions, Doubts, Support, Notifications, LiveClasses, Profile, Referrals } from "@/pages/student/Misc";

import TeacherDashboard from "@/pages/teacher/Dashboard";
import QuestionBank from "@/pages/teacher/QuestionBank";
import TestBuilder from "@/pages/teacher/TestBuilder";
import TeacherMocks from "@/pages/teacher/TeacherMocks";
import CourseBuilder from "@/pages/teacher/CourseBuilder";
import OcrImport from "@/pages/teacher/OcrImport";
import OcrReview from "@/pages/teacher/OcrReview";
import MyContent from "@/pages/teacher/MyContent";
import CopyChecking from "@/pages/teacher/CopyChecking";
import TeacherDoubts from "@/pages/teacher/Doubts";
import TeacherProfile from "@/pages/teacher/Profile";

import AdminDashboard from "@/pages/admin/Dashboard";
import Students from "@/pages/admin/Students";
import StudentDetail from "@/pages/admin/StudentDetail";
import Teachers from "@/pages/admin/Teachers";
import TeacherDetail from "@/pages/admin/TeacherDetail";
import Approvals from "@/pages/admin/Approvals";
import Taxonomy from "@/pages/admin/Taxonomy";
import CourseTaxonomy from "@/pages/admin/CourseTaxonomy";
import AdminReferrals from "@/pages/admin/Referrals";
import AdminPlans from "@/pages/admin/Plans";
import Coupons from "@/pages/admin/Coupons";
import AdminCurrentAffairs from "@/pages/admin/CurrentAffairs";
import AuditLogs from "@/pages/admin/AuditLogs";

function Guard({ roles, children, bare }) {
  const { user } = useAuth();
  if (user === null) return <PageLoader label="Preparing your workspace..." />;
  if (!user) return <Navigate to="/auth" replace />;
  if (roles && !roles.includes(user.role)) {
    const home = { student: "/app", teacher: "/teacher", admin: "/admin" }[user.role];
    return <Navigate to={home} replace />;
  }
  return bare ? children : <Shell>{children}</Shell>;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <PageLoader />;
  if (user) return <Navigate to={{ student: "/app", teacher: "/teacher", admin: "/admin" }[user.role]} replace />;
  return <Landing />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/search" element={<Guard>{<SearchPage />}</Guard>} />

        {/* Student */}
        <Route path="/app" element={<Guard roles={["student"]}><Dashboard /></Guard>} />
        <Route path="/app/courses" element={<Guard roles={["student"]}><Courses /></Guard>} />
        <Route path="/app/courses/:id" element={<Guard roles={["student"]}><CourseDetail /></Guard>} />
        <Route path="/app/tests" element={<Guard roles={["student"]}><TestSeries /></Guard>} />
        <Route path="/app/practice" element={<Guard roles={["student"]}><TestCatalog type="practice" title="Practice Sets" /></Guard>} />
        <Route path="/app/pyp" element={<Guard roles={["student"]}><Pyp /></Guard>} />
        <Route path="/app/daily-quiz" element={<Guard roles={["student"]}><TestCatalog type="daily_quiz" title="Daily Quiz" /></Guard>} />
        <Route path="/exam/:attemptId" element={<Guard roles={["student"]} bare><ExamInterface /></Guard>} />
        <Route path="/results/:attemptId" element={<Guard roles={["student"]}><Result /></Guard>} />
        <Route path="/app/performance" element={<Guard roles={["student"]}><Performance /></Guard>} />
        <Route path="/app/current-affairs" element={<Guard roles={["student"]}><CurrentAffairs /></Guard>} />
        <Route path="/app/saved" element={<Guard roles={["student"]}><Saved /></Guard>} />
        <Route path="/app/subscriptions" element={<Guard roles={["student"]}><Subscriptions /></Guard>} />
        <Route path="/app/doubts" element={<Guard roles={["student"]}><Doubts /></Guard>} />
        <Route path="/app/support" element={<Guard roles={["student"]}><Support /></Guard>} />
        <Route path="/app/notifications" element={<Guard roles={["student"]}><Notifications /></Guard>} />
        <Route path="/app/referrals" element={<Guard roles={["student"]}><Referrals /></Guard>} />
        <Route path="/app/live" element={<Guard roles={["student"]}><LiveClasses /></Guard>} />
        <Route path="/app/profile" element={<Guard roles={["student"]}><Profile /></Guard>} />

        {/* Teacher */}
        <Route path="/teacher" element={<Guard roles={["teacher"]}><TeacherDashboard /></Guard>} />
        <Route path="/teacher/courses" element={<Guard roles={["teacher"]}><CourseBuilder /></Guard>} />
        <Route path="/teacher/questions" element={<Guard roles={["teacher"]}><QuestionBank /></Guard>} />
        <Route path="/teacher/tests" element={<Guard roles={["teacher"]}><TestBuilder /></Guard>} />
        <Route path="/teacher/mocks" element={<Guard roles={["teacher"]}><TeacherMocks /></Guard>} />
        <Route path="/teacher/ocr" element={<Guard roles={["teacher"]}><OcrImport /></Guard>} />
        <Route path="/teacher/ocr/:jobId" element={<Guard roles={["teacher"]}><OcrReview /></Guard>} />
        <Route path="/teacher/content" element={<Guard roles={["teacher"]}><MyContent /></Guard>} />
        <Route path="/teacher/doubts" element={<Guard roles={["teacher"]}><TeacherDoubts /></Guard>} />
        <Route path="/teacher/copy-checking" element={<Guard roles={["teacher"]}><CopyChecking /></Guard>} />
        <Route path="/teacher/profile" element={<Guard roles={["teacher"]}><TeacherProfile /></Guard>} />

        {/* Admin */}
        <Route path="/admin" element={<Guard roles={["admin"]}><AdminDashboard /></Guard>} />
        <Route path="/admin/students" element={<Guard roles={["admin"]}><Students /></Guard>} />
        <Route path="/admin/students/:id" element={<Guard roles={["admin"]}><StudentDetail /></Guard>} />
        <Route path="/admin/teachers" element={<Guard roles={["admin"]}><Teachers /></Guard>} />
        <Route path="/admin/teachers/:id" element={<Guard roles={["admin"]}><TeacherDetail /></Guard>} />
        <Route path="/admin/approvals" element={<Guard roles={["admin"]}><Approvals /></Guard>} />
        <Route path="/admin/taxonomy" element={<Guard roles={["admin"]}><Taxonomy /></Guard>} />
        <Route path="/admin/course-taxonomy" element={<Guard roles={["admin"]}><CourseTaxonomy /></Guard>} />
        <Route path="/admin/coupons" element={<Guard roles={["admin"]}><Coupons /></Guard>} />
        <Route path="/admin/referrals" element={<Guard roles={["admin"]}><AdminReferrals /></Guard>} />
        <Route path="/admin/plans" element={<Guard roles={["admin"]}><AdminPlans /></Guard>} />
        <Route path="/admin/current-affairs" element={<Guard roles={["admin"]}><AdminCurrentAffairs /></Guard>} />
        <Route path="/admin/audit" element={<Guard roles={["admin"]}><AuditLogs /></Guard>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
